import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTheme, useMediaQuery, Box, TextField, Button, 
    CircularProgress, Typography, FormControlLabel, Checkbox,  
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const CountryPage = () => {
    useAuthCheck();
    const navigate = useNavigate();
    const { countryId } = useParams();    
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [country, setCountry] = useState({});

    const initialValues = {
        countryCode: country.countryCode || '',
        countryName: country.countryName || '',
        nameLocal: country.nameLocal || '',
        continent: country.continent || '',
        region: country.region || '',
        capitalName: country.capitalName || '',
        travelZone: country.travelZone || '',
        isDefault: country.isDefault !== undefined ? country.isDefault : false,
    };
    
    
    useEffect(() => {     
        const fetchCountry = async () => {
            try {
                setLoading(true);

                if (countryId) {                    
                    const url = `/countries/${countryId}`;
                    const response = await api.get(url);     

                    setCountry(response.data);
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchCountry();
    }, [countryId]); 
    
    const checkoutSchema = yup.object().shape({
        countryCode: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = countryId ? "PUT" : "POST";
            const url = countryId
                ? `/countries/update/${countryId}` 
                : '/countries/new'

            const res = await api({
                method,
                url,
                data: {
                    countryCode: values.countryCode,
                    countryName: values.countryName,
                    nameLocal: values.nameLocal,
                    continent: values.continent,
                    region: values.region,
                    capitalName: values.capitalName,
                    travelZone: values.travelZone,
                    isDefault: values.isDefault,
                },
            });

            if (![200, 201].includes(res.status)) {
                toast.error("Failed to submit the form!");
                return;
            }
            
            toast.success(countryId ? 'Country updated successfully' : 'Country created successfully');
            // onSubmitProps.resetForm();
            navigate("/countries");
        } catch (error) {
            setError(error);
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Country"
                    subtitle={`${loading ? 'Loading...' : countryId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/countries")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    gap={2}
                                    sx={{
                                        '& > div': { gridColumn: isDesktop ? undefined : 'span 12' },
                                    }}
                                >                                        
                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Country Code"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.countryCode}
                                        name="countryCode"
                                        error={!!touched.countryCode && !!errors.countryCode}
                                        helperText={touched.countryCode && errors.countryCode}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Country Name"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.countryName}
                                        name="countryName"
                                        error={!!touched.countryName && !!errors.countryName}
                                        helperText={touched.countryName && errors.countryName}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Name Local"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.nameLocal}
                                        name="nameLocal"
                                        error={!!touched.nameLocal && !!errors.nameLocal}
                                        helperText={touched.nameLocal && errors.nameLocal}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Continent"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.continent}
                                        name="continent"
                                        error={!!touched.continent && !!errors.continent}
                                        helperText={touched.continent && errors.continent}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Region"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.region}
                                        name="region"
                                        error={!!touched.region && !!errors.region}
                                        helperText={touched.region && errors.region}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Capital Name"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.capitalName}
                                        name="capitalName"
                                        error={!!touched.capitalName && !!errors.capitalName}
                                        helperText={touched.capitalName && errors.capitalName}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Travel Zone"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.travelZone}
                                        name="travelZone"
                                        error={!!touched.travelZone && !!errors.travelZone}
                                        helperText={touched.travelZone && errors.travelZone}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 6' }}
                                        control={
                                            <Checkbox
                                                checked={values.isDefault}
                                                onChange={handleChange}
                                                name="isDefault"
                                                color="primary"
                                            />
                                        }
                                        label="Is Default"
                                    />

                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {countryId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default CountryPage;
